//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { Status } from '@hcengineering/platform'
import { get, writable } from 'svelte/store'
import type { Action } from './types'

/**
 * The single-sidebar shell (DESIGN.md §2).
 *
 * The workbench sidebar publishes an element under the active app's row.
 * Whatever navigator the active app renders — the workbench Navigator or an
 * app's own (Chat, Inbox, Planner, Settings…) — is moved into that element
 * with the `shellNavigator` action, so there is one navigation column instead
 * of an app rail plus a navigator panel.
 *
 * When the sidebar is collapsed to its icon rail there is no target, and the
 * navigator stays where the app rendered it (docked or floating), exactly as
 * before.
 *
 * @public
 */
export const shellNavTarget = writable<HTMLElement | undefined>(undefined)

/**
 * True while the workbench sidebar hosts the root-bar extensions, settings and
 * clock, so the root status bar only needs to appear for connection or
 * maintenance messages (and as the desktop app's drag area).
 *
 * @public
 */
export const rootBarHosted = writable<boolean>(false)

/**
 * The connection's status message (Root's PlatformEvent). While the tray hosts
 * the root bar, the tray shows it in its live activity instead of the bar
 * opening over the page and pushing it down (DESIGN.md §1.3).
 *
 * @public
 */
export const shellStatus = writable<Status | undefined>(undefined)

/**
 * Unread marks for apps whose messages live outside Avocado's own
 * notifications (Chat when it is the company chat on Matrix), by app alias.
 * The sidebar shows the app row's dot while its value is true.
 *
 * @public
 */
export const appUnread = writable<Record<string, boolean>>({})

/**
 * Component apps that bring their own navigation inside their page (Chat as
 * the company chat), by alias: their sidebar row is a plain link, no menu.
 *
 * @public
 */
export const appsWithoutMenu = writable<Set<string>>(new Set())

const PORTALED = 'fz-shell-portaled'

/**
 * Svelte action: put it on the element that holds an app's navigator content
 * (normally `.antiPanel-wrap__content` inside `.antiPanel-navigator`).
 *
 * @public
 */
export function shellNavigator (
  node: HTMLElement,
  enabled: boolean = true
): { update: (enabled: boolean) => void, destroy: () => void } {
  const host = node.parentElement
  const placeholder = document.createComment('shell-navigator')
  let target: HTMLElement | undefined
  let isEnabled = enabled

  function mount (t: HTMLElement | null | undefined): void {
    // Svelte's bind:this sets null when the host goes away; throwing here
    // would abort Svelte's flush and freeze every later update.
    if (!isEnabled || t == null || target !== undefined) return
    node.parentNode?.insertBefore(placeholder, node)
    t.appendChild(node)
    host?.classList.add(PORTALED)
    target = t
  }

  function unmount (): void {
    if (target === undefined) return
    if (placeholder.parentNode != null) {
      placeholder.parentNode.insertBefore(node, placeholder)
      placeholder.remove()
    }
    host?.classList.remove(PORTALED)
    target = undefined
  }

  const unsubscribe = shellNavTarget.subscribe((t) => {
    if ((t ?? undefined) === target) return
    unmount()
    mount(t)
  })

  return {
    update (value: boolean) {
      isEnabled = value
      unmount()
      mount(get(shellNavTarget))
    },
    destroy () {
      unsubscribe()
      // The component is going away. Svelte only detaches its top-level nodes,
      // so a navigator living in the sidebar has to be removed here.
      if (target !== undefined && node.parentNode === target) {
        node.remove()
      }
      placeholder.remove()
      host?.classList.remove(PORTALED)
      target = undefined
    }
  }
}

/**
 * Avocado v3: where a page's band tools go. A page whose band is drawn by the
 * shell (a project's tabs row, `SpaceSpecialTabs`) publishes an element here;
 * the page's header puts its search, filter, view settings and primary action
 * into it with `use:bandPortal`, so they sit in the band, left of the tray,
 * instead of in a row of their own (DESIGN.md §2.2).
 */
export const bandToolsTarget = writable<HTMLElement | undefined>(undefined)

/**
 * Moves the node into `target` while there is one; Svelte still owns it and
 * removes it from wherever it is when the component goes away.
 */
export function bandPortal (
  node: HTMLElement,
  target: HTMLElement | undefined
): { update: (target: HTMLElement | undefined) => void, destroy: () => void } {
  const place = (t: HTMLElement | undefined): void => {
    if (t != null && node.parentElement !== t) t.appendChild(node)
  }
  place(target)
  return {
    update: place,
    destroy: () => {
      node.remove()
    }
  }
}

/**
 * Avocado v3: the open page's rarely used commands (analytics, column
 * settings…). The band's "⋯" menu lists them under the space's other
 * sections, so the band itself keeps only the everyday tools.
 */
export const bandMenuActions = writable<Action[]>([])
